import { useState, useRef, useCallback } from 'react';
import { Camera, Upload, X, Loader2, CheckCircle, AlertCircle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import API from '@/utils/api';

/**
 * InsuranceCardScanner - Captures or uploads insurance card images
 * and extracts information using AI (Gemini Vision)
 */
const InsuranceCardScanner = ({ onExtracted, onClose }) => {
  const [mode, setMode] = useState(null); // 'camera' or 'upload'
  const [processing, setProcessing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [error, setError] = useState(null);
  
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Start camera
  const startCamera = useCallback(async () => {
    setMode('camera');
    setError(null);
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment', // Use back camera on mobile
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });
      
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error('Camera error:', err);
      setError('Could not access camera. Please check permissions or try uploading a photo instead.');
      setMode(null);
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  // Capture photo from camera
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedImage(imageData);
    setPreview(imageData);
    
    stopCamera();
    setMode('preview');
  }, [stopCamera]);

  // Handle file upload
  const handleFileUpload = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WebP image');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setCapturedImage(e.target.result);
      setPreview(e.target.result);
      setMode('preview');
    };
    reader.readAsDataURL(file);
  }, []);

  // Process image with AI
  const processImage = useCallback(async () => {
    if (!capturedImage) return;
    
    setProcessing(true);
    setError(null);
    
    try {
      // Extract base64 data (remove data:image/...;base64, prefix)
      const base64Match = capturedImage.match(/^data:([^;]+);base64,(.+)$/);
      if (!base64Match) {
        throw new Error('Invalid image format');
      }
      
      const mimeType = base64Match[1];
      const imageBase64 = base64Match[2];
      
      const response = await API.post('/scan-insurance-card', {
        image_base64: imageBase64,
        mime_type: mimeType
      });
      
      if (response.data?.success && response.data?.data) {
        setExtractedData(response.data.data);
        setMode('results');
        toast.success('Insurance card scanned successfully!');
      } else {
        throw new Error('Failed to extract data');
      }
    } catch (err) {
      console.error('Processing error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to process image';
      setError(errorMsg);
      toast.error('Could not read card', { description: errorMsg });
    } finally {
      setProcessing(false);
    }
  }, [capturedImage]);

  // Use extracted data
  const useExtractedData = useCallback(() => {
    if (extractedData && onExtracted) {
      onExtracted({
        provider: extractedData.provider || '',
        policy_number: extractedData.policy_number || extractedData.member_id || '',
        group_number: extractedData.group_number || '',
        phone: extractedData.phone || '',
        member_name: extractedData.member_name || '',
        plan_type: extractedData.plan_type || '',
        rx_bin: extractedData.rx_bin || '',
        rx_pcn: extractedData.rx_pcn || '',
        rx_group: extractedData.rx_group || '',
      });
    }
    onClose?.();
  }, [extractedData, onExtracted, onClose]);

  // Reset to start
  const reset = useCallback(() => {
    stopCamera();
    setMode(null);
    setPreview(null);
    setCapturedImage(null);
    setExtractedData(null);
    setError(null);
    setProcessing(false);
  }, [stopCamera]);

  // Cleanup on unmount
  const handleClose = useCallback(() => {
    stopCamera();
    onClose?.();
  }, [stopCamera, onClose]);

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={(e) => {
        // Click-outside-to-close
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-slate-800">
            {mode === 'results' ? 'Scan Results' : 'Scan Insurance Card'}
          </h2>
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {/* Initial Mode Selection */}
          {!mode && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 text-center mb-6">
                Take a photo or upload an image of your insurance card to automatically fill in your information.
              </p>
              
              <Button
                variant="outline"
                className="w-full h-24 flex flex-col gap-2 border-2 border-dashed hover:border-indigo-400 hover:bg-indigo-50"
                onClick={startCamera}
              >
                <Camera className="h-8 w-8 text-indigo-500" />
                <span className="text-slate-700">Take a Photo</span>
              </Button>
              
              <Button
                variant="outline"
                className="w-full h-24 flex flex-col gap-2 border-2 border-dashed hover:border-indigo-400 hover:bg-indigo-50"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-8 w-8 text-indigo-500" />
                <span className="text-slate-700">Upload Image</span>
              </Button>
              
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileUpload}
              />
              
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* Camera Mode */}
          {mode === 'camera' && (
            <div className="space-y-4">
              <div className="relative aspect-[4/3] bg-black rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  playsInline
                  autoPlay
                  muted
                />
                <canvas ref={canvasRef} className="hidden" />
                
                {/* Card outline guide */}
                <div className="absolute inset-4 border-2 border-white/50 rounded-lg pointer-events-none">
                  <div className="absolute top-2 left-2 w-6 h-6 border-l-2 border-t-2 border-white"></div>
                  <div className="absolute top-2 right-2 w-6 h-6 border-r-2 border-t-2 border-white"></div>
                  <div className="absolute bottom-2 left-2 w-6 h-6 border-l-2 border-b-2 border-white"></div>
                  <div className="absolute bottom-2 right-2 w-6 h-6 border-r-2 border-b-2 border-white"></div>
                </div>
              </div>
              
              <p className="text-sm text-slate-500 text-center">
                Position the card within the frame
              </p>
              
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={reset}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700" onClick={capturePhoto}>
                  <Camera className="h-4 w-4 mr-2" />
                  Capture
                </Button>
              </div>
            </div>
          )}

          {/* Preview Mode */}
          {mode === 'preview' && preview && (
            <div className="space-y-4">
              <div className="relative aspect-[4/3] bg-slate-100 rounded-lg overflow-hidden">
                <img
                  src={preview}
                  alt="Insurance card preview"
                  className="w-full h-full object-contain"
                />
              </div>
              
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={reset}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Retake
                </Button>
                <Button 
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700" 
                  onClick={processImage}
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Extract Info
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Results Mode */}
          {mode === 'results' && extractedData && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-green-800">Information extracted successfully!</span>
              </div>
              
              <div className="space-y-3">
                {extractedData.provider && (
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Provider</span>
                    <span className="text-sm font-medium text-slate-800">{extractedData.provider}</span>
                  </div>
                )}
                {(extractedData.policy_number || extractedData.member_id) && (
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Policy/Member ID</span>
                    <span className="text-sm font-medium text-slate-800">
                      {extractedData.policy_number || extractedData.member_id}
                    </span>
                  </div>
                )}
                {extractedData.group_number && (
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Group Number</span>
                    <span className="text-sm font-medium text-slate-800">{extractedData.group_number}</span>
                  </div>
                )}
                {extractedData.member_name && (
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Member Name</span>
                    <span className="text-sm font-medium text-slate-800">{extractedData.member_name}</span>
                  </div>
                )}
                {extractedData.phone && (
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Phone</span>
                    <span className="text-sm font-medium text-slate-800">{extractedData.phone}</span>
                  </div>
                )}
                {extractedData.plan_type && (
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Plan Type</span>
                    <span className="text-sm font-medium text-slate-800">{extractedData.plan_type}</span>
                  </div>
                )}
                {extractedData.rx_bin && (
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-500">RX BIN</span>
                    <span className="text-sm font-medium text-slate-800">{extractedData.rx_bin}</span>
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={reset}>
                  Scan Again
                </Button>
                <Button 
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700" 
                  onClick={useExtractedData}
                >
                  Use This Info
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InsuranceCardScanner;
